import { CircularProgress, useMediaQuery, useTheme, Icon, Tooltip } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import Footer from "examples/Footer";
import BusinessNavbar from "examples/Navbars/DashboardNavbar/businessNavbar";

import StyledBoard from "../StyledBoard";

import CardItem from "./CardItem";
import ColumnHeader from "./ColumnHeader";
import NewColumnForm from "./NewColumnForm";
import { useKanbanData } from "./useKanbanData";
import { useKanbanTracker } from "./useKanbanTracker";

const KanbanBoard = () => {
  const { session } = useBusinessContext();
  const businessId = session?.selectedBusiness?.id;
  const { board, setBoard, loading, vendors, statusOptions } = useKanbanData(businessId);
  const tracker = useKanbanTracker(board, setBoard);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));

  const createBadge = (card) => {
    if (!card?.due_date) return null;

    const dueDate = new Date(card.due_date);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // If due date has passed
    if (diffDays < 0) {
      return {
        color: "error",
        label: `Scaduto da ${Math.abs(diffDays)} giorni`,
      };
    }
    // If due within 3 days
    if (diffDays <= 3) {
      return {
        color: "warning",
        label: `Scade fra ${diffDays} giorni`,
      };
    }
    // If due within a week
    if (diffDays <= 7) {
      return {
        color: "primary",
        label: `Scade fra ${diffDays} giorni`,
      };
    }
    // If due date is far away
    return {
      color: "success",
      label: new Date(card.due_date).toLocaleDateString(),
    };
  };
  if (!businessId || loading) {
    return (
      <div>
        <BusinessNavbar />
        <ArgonBox py={3} display="flex" justifyContent="center" alignItems="center" height="60vh">
          <ArgonTypography variant="h5" color="text" fontWeight="regular" mr={2}>
            Loading Kanban board...
          </ArgonTypography>
          <CircularProgress size={24} />
        </ArgonBox>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <BusinessNavbar />

      <ArgonBox py={3} px={isTablet ? 1 : 3}>
        {/* Top toolbar with relative positioning for the form */}
        <ArgonBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{ position: "relative" }}
        >
          {/* New Column Form positioned absolute */}
          <NewColumnForm
            open={tracker.newColumnForm}
            title={tracker.newColumnTitle}
            onChange={(e) => tracker.setNewColumnTitle(e.target.value)}
            onSubmit={tracker.handleCreateColumn}
            onClose={() => tracker.setNewColumnForm(false)}
          />

          <ArgonTypography variant="h5" fontWeight="medium">
            Gestione Leads
          </ArgonTypography>

          <ArgonBox display="flex" alignItems="center">
            <Tooltip
              title={
                tracker.toggleDeleteMode
                  ? "Esci dalla Modalità di cancellazione"
                  : "Modalità di cancellazione"
              }
            >
              <ArgonButton
                variant="gradient"
                color={tracker.toggleDeleteMode ? "error" : "light"}
                size="small"
                onClick={tracker.handleToggleDeleteMode}
                sx={{ display: "flex", alignItems: "center", transition: "all 0.2s", mr: 2 }}
              >
                <Icon sx={{ fontSize: 16, mr: 0.5 }}>
                  {tracker.toggleDeleteMode ? "cancel" : "delete"}
                </Icon>
                {tracker.toggleDeleteMode ? "Esci Delete Mode" : "Delete Mode"}
              </ArgonButton>
            </Tooltip>
            <Tooltip title="Aggiungi nuova colonna">
              <ArgonButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => tracker.setNewColumnForm(true)}
                sx={{ display: "flex", alignItems: "center", transition: "transform 0.2s", ml: 2 }}
              >
                <Icon sx={{ fontSize: 16, mr: 0.5 }}>add</Icon>
                Aggiungi Colonna
              </ArgonButton>
            </Tooltip>
          </ArgonBox>
        </ArgonBox>

        {/* Board container with styling overrides */}
        <ArgonBox
          position="relative"
          my={2}
          sx={({ palette, functions, borders }) => ({
            "& .react-kanban-column": {
              backgroundColor: palette.background.paper,
              width: isTablet ? functions.pxToRem(300) : functions.pxToRem(450),
              margin: `0 ${functions.pxToRem(10)}`,
              padding: functions.pxToRem(20),
              borderRadius: borders.borderRadius.xl,
              marginBottom: functions.pxToRem(20),
            },
            "& .react-kanban-card--dragging": {
              transform: "rotate(3deg) scale(1.05)",
              cursor: "grabbing",
              zIndex: 1000,
            },
            "& .react-kanban-card": {
              cursor: "grab",
            },
            "& .react-kanban-column > div:nth-child(2) > div > div": {
              display: "block!important",
            },
          })}
        >
          <ArgonBox display="flex" alignItems="flex-start" width="100%">
            {/* Kanban board with custom headers and cards */}
            <StyledBoard
              initialBoard={board}
              disableCardDrag={tracker.toggleDeleteMode}
              disableColumnDrag={tracker.toggleDeleteMode}
              onCardDragEnd={tracker.handleCardMove}
              onColumnDragEnd={tracker.handleReorderColumns}
              renderColumnHeader={(col, bag) => (
                <ColumnHeader
                  column={col}
                  deleteMode={tracker.toggleDeleteMode}
                  onDeleteColumn={tracker.handleDeleteColumn}
                  onAddCard={tracker.handleCreateCard}
                />
              )}
              renderCard={(card, bag) => {
                return (
                  <CardItem
                    card={card}
                    dragging={bag.dragging}
                    deleteMode={tracker.toggleDeleteMode}
                    onDelete={tracker.handleDeleteCard}
                    statusOptions={statusOptions || []}
                    vendors={vendors || []}
                    badge={card.originalData ? createBadge(card.originalData) : null}
                    numberOfInteractions={card.originalData?.interactionsnumber || 0}
                    assignedTo={
                      card.originalData
                        ? {
                            image: card.originalData.vendorpic,
                            name: card.originalData.vendorname,
                          }
                        : null
                    }
                  />
                );
              }}
            />
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>

      <Footer />
    </div>
  );
};

export default KanbanBoard;
